import { ErrorMessage } from '@/components/common/ErrorMessage';
import { PanelSkeleton } from '@/components/common/Skeleton';
import { CARD_PADDED, ICON_TILE, PANEL_TITLE, SECTION_LABEL } from '@/components/common/surfaces';
import { useMaterials } from '@/hooks/useMaterials';
import { useStepDocument } from '@/hooks/useStepDocument';
import type { MaterialModel } from '@/types/material.types';
import { Cube, WarningCircle } from '@phosphor-icons/react';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/** Placeholder for a value the STEP file or the catalog match leaves blank. */
const EMPTY_VALUE = '—';

interface MaterialsPanelProps {
  /** Id of the project's STEP document, or `null` while no STEP has been uploaded. */
  modelId: string | null;
}

/** A material reads best as its catalog name plus the norm it is filed under, when it has one. */
function materialLabel(material: MaterialModel): string {
  return material.norm_code ? `${material.name} (${material.norm_code})` : material.name;
}

interface DefinitionRowProps {
  label: string;
  value: string;
  muted?: boolean;
}

const DefinitionRow: FC<DefinitionRowProps> = ({ label, value, muted = false }) => (
  <div className="flex items-baseline gap-2">
    <span className="shrink-0 text-sm text-gray-500">{label}:</span>
    <span className={`break-words text-sm font-medium ${muted ? 'text-gray-400' : 'text-gray-900'}`}>{value}</span>
  </div>
);

/**
 * The material of the project's STEP document: what the file itself says and what the catalog
 * resolved that to.
 *
 * Read-only by design — the material comes from the STEP file, and the catalog's aliases decide
 * what it resolves to. The two sides are shown side by side because a STEP can name a material
 * the catalog has never seen, and that combination — a raw string with no catalog row behind it
 * — is exactly the case where no cutting data can be looked up, so it gets called out rather
 * than rendered as an empty cell. The fix for it belongs in the catalog's aliases, not here.
 */
export const MaterialsPanel: FC<MaterialsPanelProps> = ({ modelId }) => {
  const { t } = useTranslation();

  const { stepDocument, isLoading: isLoadingDocument, error: documentError } = useStepDocument(modelId);
  const { materials, isLoading: isLoadingMaterials, error: materialsError } = useMaterials();

  const nameById = useMemo(
    () => new Map(materials.map((material) => [material.id, materialLabel(material)])),
    [materials]
  );

  /** Catalog name for a resolved id — falls back to a marker when the row is not in the catalog. */
  const catalogName = (materialId: string | null): string => {
    if (!materialId) return EMPTY_VALUE;
    return nameById.get(materialId) ?? t('materials.unknownMaterial');
  };

  // The STEP names a material but nothing in the catalog matched it — the case that leaves the
  // project without cutting data.
  const isUnmatched = Boolean(stepDocument?.material_1) && !stepDocument?.material_id;

  return (
    <div className={CARD_PADDED}>
      <div className="flex min-w-0 items-center gap-3">
        <span className={ICON_TILE}>
          <Cube size={16} aria-hidden="true" />
        </span>
        <h2 className={PANEL_TITLE}>{t('materials.title')}</h2>
      </div>

      <div className="mt-6">
        {!modelId ? (
          <p className="m-0 text-sm text-gray-500">{t('materials.noStepDocument')}</p>
        ) : isLoadingDocument || isLoadingMaterials ? (
          /* Both fetches gate the card: rendering a resolved material before the catalog arrives
           would briefly label a perfectly good match as unknown. */
          <PanelSkeleton lines={5} />
        ) : documentError ? (
          <ErrorMessage message={t(documentError)} />
        ) : (
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <h3 className={SECTION_LABEL}>{t('materials.fromStep')}</h3>
                <DefinitionRow
                  label={t('materials.primary')}
                  value={stepDocument?.material_1 || EMPTY_VALUE}
                  muted={!stepDocument?.material_1}
                />
                <DefinitionRow
                  label={t('materials.fallback')}
                  value={stepDocument?.material_2 || EMPTY_VALUE}
                  muted={!stepDocument?.material_2}
                />
              </div>

              <div className="flex flex-col gap-2">
                <h3 className={SECTION_LABEL}>{t('materials.fromCatalog')}</h3>
                <DefinitionRow
                  label={t('materials.primary')}
                  value={catalogName(stepDocument?.material_id ?? null)}
                  muted={!stepDocument?.material_id}
                />
                <DefinitionRow
                  label={t('materials.fallback')}
                  value={catalogName(stepDocument?.fallback_material_id ?? null)}
                  muted={!stepDocument?.fallback_material_id}
                />
              </div>
            </div>

            {isUnmatched && (
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <WarningCircle size={20} className="mt-0.5 shrink-0 text-amber-600" aria-hidden={true} />
                <div className="flex flex-col gap-1">
                  <p className="m-0 text-sm font-semibold text-amber-700">{t('materials.unmatchedTitle')}</p>
                  <p className="m-0 text-sm text-amber-700 [text-wrap:pretty]">
                    {t('materials.unmatchedMessage', { material: stepDocument?.material_1 ?? '' })}
                  </p>
                </div>
              </div>
            )}

            {materialsError && <p className="m-0 text-sm text-gray-500">{t(materialsError)}</p>}
          </div>
        )}
      </div>
    </div>
  );
};
